import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Printer, Truck, MapPin, Clock, Edit, Send, FileText, User, Link2, Copy, MessageCircle, Mail as MailIcon, RefreshCw, Receipt, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { EmailStatus } from '../components/EmailStatus';
import { SignaturePanel } from '../components/SignaturePanel';
import DispatchSupplierDialog from '../components/DispatchSupplierDialog';
import CancelJobDialog from '../components/CancelJobDialog';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COMPANY_INFO = {
  name: "NJK Logistics Pte Ltd",
  address: "212A Punggol Walk #12-741 Singapore 821212",
  phone: "98366186",
  email: "inquiry@njklogistics.com"
};

const PUBLIC_SITE_URL = process.env.REACT_APP_PUBLIC_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '');

const ShareSigningLink = ({ deliveryOrder, onUpdate }) => {
  const [token, setToken] = useState(deliveryOrder.sign_token || '');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setToken(deliveryOrder.sign_token || '');
  }, [deliveryOrder.sign_token]);

  // Public URL = current site origin + /sign/do/<token>
  const publicUrl = token ? `${PUBLIC_SITE_URL}/sign/do/${token}` : '';

  const ensureToken = async () => {
    if (token) return token;
    setBusy(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/public/delivery-orders/${deliveryOrder.id}/generate-sign-token`);
      setToken(res.data.sign_token);
      onUpdate?.();
      return res.data.sign_token;
    } catch (e) {
      toast.error('Failed to create signing link');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    const t = await ensureToken();
    if (!t) return;
    const url = `${PUBLIC_SITE_URL}/sign/do/${t}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Signing link copied to clipboard');
    } catch {
      // Fallback for older browsers
      window.prompt('Copy this link:', url);
    }
  };

  const openWhatsApp = async () => {
    const t = await ensureToken();
    if (!t) return;
    const url = `${PUBLIC_SITE_URL}/sign/do/${t}`;
    const msg = encodeURIComponent(
      `NJK Logistics — Delivery Order ${deliveryOrder.document_number}\n\nPlease sign the delivery order using this link:\n${url}`
    );
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  const emailDriver = async () => {
    const t = await ensureToken();
    if (!t) return;
    const url = `${PUBLIC_SITE_URL}/sign/do/${t}`;
    const subject = encodeURIComponent(`Signing Link — Delivery Order ${deliveryOrder.document_number}`);
    const body = encodeURIComponent(
      `Hi,\n\nPlease open the link below on your phone or tablet to capture the driver and customer signatures for DO ${deliveryOrder.document_number}:\n\n${url}\n\nThanks,\nNJK Logistics`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const revoke = async () => {
    if (!window.confirm('Generate a new signing link? The old link will stop working.')) return;
    setBusy(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/public/delivery-orders/${deliveryOrder.id}/revoke-sign-token`);
      setToken(res.data.sign_token);
      onUpdate?.();
      toast.success('New signing link generated. Old link is now invalid.');
    } catch (e) {
      toast.error('Failed to regenerate link');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-t pt-6 mt-6 print:hidden" data-testid="share-signing-link">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <Link2 size={20} className="text-blue-600" /> Share signing link with driver
        </h3>
        {token && (
          <Button variant="ghost" size="sm" onClick={revoke} disabled={busy} data-testid="revoke-token-btn">
            <RefreshCw size={14} className="mr-1" /> New link
          </Button>
        )}
      </div>
      <p className="text-sm text-gray-600 mb-3">
        Send this link to your driver. Anyone with the link can sign on a phone or tablet — no admin login required.
      </p>

      {publicUrl && (
        <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 mb-3 text-sm text-gray-700 break-all">
          {publicUrl}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button onClick={copyLink} disabled={busy} variant="outline" data-testid="copy-link-btn">
          <Copy size={14} className="mr-1" /> Copy link
        </Button>
        <Button onClick={openWhatsApp} disabled={busy} className="bg-[#25D366] hover:bg-[#1ebe57] text-white" data-testid="whatsapp-link-btn">
          <MessageCircle size={14} className="mr-1" /> WhatsApp
        </Button>
        <Button onClick={emailDriver} disabled={busy} variant="outline" data-testid="email-link-btn">
          <MailIcon size={14} className="mr-1" /> Email to driver
        </Button>
      </div>
    </div>
  );
};

export const DeliveryOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [deliveryOrder, setDeliveryOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [requestingRating, setRequestingRating] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [linkedInvoice, setLinkedInvoice] = useState(null);

  useEffect(() => {
    if (!deliveryOrder?.invoice_id) { setLinkedInvoice(null); return; }
    axios.get(`${BACKEND_URL}/api/invoices/${deliveryOrder.invoice_id}`)
      .then((res) => setLinkedInvoice({ id: deliveryOrder.invoice_id, number: res.data.document_number }))
      .catch(() => setLinkedInvoice(null));
  }, [deliveryOrder?.invoice_id]);

  const createInvoiceFromDO = async () => {
    if (!window.confirm('Create an invoice pre-filled from this delivery order?')) return;
    setCreatingInvoice(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/invoices/from-do/${id}`);
      toast.success(`Invoice ${res.data.document_number} created`);
      navigate(`/admin/billing/invoice/${res.data.invoice_id}/edit`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create invoice'));
    } finally {
      setCreatingInvoice(false);
    }
  };

  const requestRating = async () => {
    setRequestingRating(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/delivery-orders/${id}/request-rating`);
      toast.success(res.data.message);
      fetchDeliveryOrder();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send rating request'));
    } finally {
      setRequestingRating(false);
    }
  };

  useEffect(() => {
    fetchDeliveryOrder();
  }, [id]);

  const fetchDeliveryOrder = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/delivery-orders/${id}`);
      setDeliveryOrder(response.data);
    } catch (error) {
      toast.error('Failed to load delivery order');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const sendToCustomer = async () => {
    if (!deliveryOrder.customer_email) {
      toast.error('Customer email is required');
      return;
    }
    
    setSendingEmail(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/delivery-orders/${id}/send`);
      if (response.data.success) {
        toast.success(response.data.message);
      }
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to send delivery order');
      toast.error(message);
    } finally {
      setSendingEmail(false);
    }
  };

  const markAsDelivered = async () => {
    setActionLoading(true);
    try {
      await axios.patch(`${BACKEND_URL}/api/delivery-orders/${id}/delivered`);
      toast.success('Marked as delivered');
      fetchDeliveryOrder();
    } catch (error) {
      toast.error('Failed to update delivery order');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-SG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!deliveryOrder) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Delivery Order Not Found</h2>
          <Button onClick={() => navigate('/admin/billing')}>Back to Billing</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-40">
      <div className="bg-white border-b sticky top-0 z-10 print:hidden">
        <div className="container mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/admin/billing')}>
                <ArrowLeft size={20} />
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{deliveryOrder.document_number}</h1>
                <p className="text-gray-600 mt-1">Delivery Order Details</p>
                {(deliveryOrder.created_by || deliveryOrder.updated_by) && (
                  <p className="text-xs text-gray-400 mt-0.5" data-testid="do-audit-info">
                    {deliveryOrder.created_by && `Created by ${deliveryOrder.created_by}${deliveryOrder.created_by_role ? ` (${deliveryOrder.created_by_role})` : ''}`}
                    {deliveryOrder.created_by && deliveryOrder.updated_by && ' • '}
                    {deliveryOrder.updated_by && `Last edited by ${deliveryOrder.updated_by}${deliveryOrder.updated_by_role ? ` (${deliveryOrder.updated_by_role})` : ''}`}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Link to={`/admin/billing/delivery-order/${id}/edit`}>
                <Button variant="outline" data-testid="edit-do-btn">
                  <Edit size={20} className="mr-2" />
                  Edit
                </Button>
              </Link>
              <Button 
                onClick={sendToCustomer}
                variant="outline"
                disabled={sendingEmail}
                className="text-orange-600 border-orange-600 hover:bg-orange-50"
                data-testid="send-do-btn"
              >
                {sendingEmail ? (
                  <>
                    <div className="w-4 h-4 border-2 border-orange-600/30 border-t-orange-600 rounded-full animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={20} className="mr-2" />
                    Send to Customer
                  </>
                )}
              </Button>
              {!deliveryOrder.delivered && (
                <Button 
                  onClick={markAsDelivered} 
                  className="bg-green-600 hover:bg-green-700 text-white"
                  disabled={actionLoading}
                  data-testid="mark-delivered-btn"
                >
                  <CheckCircle size={20} className="mr-2" />
                  Mark Delivered
                </Button>
              )}
              {deliveryOrder.delivered && (
                <Button
                  onClick={requestRating}
                  variant="outline"
                  disabled={requestingRating}
                  className="text-amber-700 border-amber-500 hover:bg-amber-50"
                  data-testid="request-rating-btn"
                >
                  <Send size={18} className="mr-2" />
                  {requestingRating ? 'Sending...' : deliveryOrder.rating_request_sent_at ? 'Re-send Rating Request' : 'Request Rating'}
                </Button>
              )}
              {!deliveryOrder.invoice_id && (
                <Button
                  onClick={createInvoiceFromDO}
                  variant="outline"
                  disabled={creatingInvoice}
                  className="text-green-700 border-green-600 hover:bg-green-50"
                  data-testid="create-invoice-from-do-btn"
                >
                  <FileText size={18} className="mr-2" />
                  {creatingInvoice ? 'Creating...' : 'Create Invoice'}
                </Button>
              )}
              {linkedInvoice && (
                <Button
                  onClick={() => navigate(`/admin/billing/invoice/${linkedInvoice.id}`)}
                  variant="outline"
                  className="text-green-700 border-green-600 hover:bg-green-50"
                  data-testid="view-linked-invoice-btn"
                >
                  <Receipt size={18} className="mr-2" />
                  Invoice {linkedInvoice.number}
                </Button>
              )}
              {deliveryOrder.driver_name && (
                <Button
                  onClick={() => window.open(`/driver/${encodeURIComponent(deliveryOrder.driver_name)}`, '_blank')}
                  variant="outline"
                  data-testid="driver-sheet-btn"
                >
                  <Truck size={18} className="mr-2" />
                  Driver Sheet
                </Button>
              )}
              <Button onClick={() => setShowDispatchModal(true)} variant="outline"
                      className="text-emerald-700 border-emerald-600 hover:bg-emerald-50"
                      data-testid="dispatch-supplier-btn">
                <MessageCircle size={20} className="mr-2" />
                Dispatch to Supplier
              </Button>
              <Button onClick={() => window.open(`${BACKEND_URL}/api/delivery-orders/${id}/pdf`, '_blank')} variant="outline" data-testid="preview-pdf-btn">
                <FileText size={20} className="mr-2" />
                Preview PDF
              </Button>
              <Button onClick={handlePrint} variant="outline" data-testid="print-do-btn">
                <Printer size={20} className="mr-2" />
                Print
              </Button>
              {deliveryOrder.status !== 'cancelled' && (
                <Button onClick={() => setShowCancelModal(true)} variant="outline"
                        className="text-red-600 border-red-500 hover:bg-red-50"
                        data-testid="cancel-job-btn">
                  <XCircle size={20} className="mr-2" />
                  Cancel Job
                </Button>
              )}
            </div>
          </div>
          <EmailStatus docType="delivery_order" docId={id} />
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 print:px-0 print:py-0">
        <div className="max-w-4xl mx-auto">
          <Card className="print:shadow-none print:border-none">
            <CardContent className="p-8">
              <div className="flex justify-between items-start mb-8 border-b pb-6">
                <div>
                  <h2 className="text-2xl font-bold text-orange-600">{COMPANY_INFO.name}</h2>
                  <p className="text-gray-600 mt-1">{COMPANY_INFO.address}</p>
                  <p className="text-gray-600">Tel: {COMPANY_INFO.phone}</p>
                  <p className="text-gray-600">Email: {COMPANY_INFO.email}</p>
                </div>
                <div className="text-right">
                  <h1 className="text-3xl font-bold text-gray-900">DELIVERY ORDER</h1>
                  <p className="text-xl font-semibold text-orange-600 mt-2">{deliveryOrder.document_number}</p>
                  {deliveryOrder.status === 'cancelled' ? (
                    <Badge className="bg-red-100 text-red-800 mt-2" data-testid="do-cancelled-badge">
                      ✕ CANCELLED
                    </Badge>
                  ) : deliveryOrder.driver_signature && deliveryOrder.customer_signature ? (
                    <Badge className="bg-green-100 text-green-800 mt-2" data-testid="do-signed-badge">
                      ✓ SIGNED
                    </Badge>
                  ) : (deliveryOrder.driver_signature || deliveryOrder.customer_signature) ? (
                    <Badge className="bg-yellow-100 text-yellow-800 mt-2">
                      PARTIAL SIGN
                    </Badge>
                  ) : (
                    <Badge className={deliveryOrder.delivered ? 'bg-green-100 text-green-800 mt-2' : 'bg-orange-100 text-orange-800 mt-2'}>
                      {deliveryOrder.delivered ? 'DELIVERED' : 'PENDING'}
                    </Badge>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Customer:</h3>
                  <p className="font-bold text-lg">{deliveryOrder.customer_name}</p>
                  <p className="text-gray-600">{deliveryOrder.customer_email}</p>
                  <p className="text-gray-600">{deliveryOrder.customer_phone}</p>
                  {deliveryOrder.po_number && (
                    <p className="text-gray-600 mt-2"><span className="font-semibold">P/O No:</span> {deliveryOrder.po_number}</p>
                  )}
                </div>
                <div className="text-right">
                  <div className="mb-2">
                    <span className="text-gray-600">DO Date: </span>
                    <span className="font-semibold">{formatDate(deliveryOrder.delivery_date || deliveryOrder.created_at)}</span>
                  </div>
                </div>
              </div>

              {/* Delivery Details */}
              <div className="bg-orange-50 p-6 rounded-lg mb-8 print:bg-white print:border print:border-gray-200">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Truck size={20} className="text-orange-600" />
                  Delivery Information
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <div className="flex items-start gap-2 mb-4">
                      <MapPin size={18} className="text-gray-500 mt-1" />
                      <div>
                        <p className="text-sm text-gray-600">Pickup Address:</p>
                        <p className="font-medium">{deliveryOrder.pickup_address || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <MapPin size={18} className="text-orange-600 mt-1" />
                      <div>
                        <p className="text-sm text-gray-600">Delivery Address:</p>
                        <p className="font-medium">{deliveryOrder.delivery_address}</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-start gap-2 mb-4">
                      <Clock size={18} className="text-gray-500 mt-1" />
                      <div>
                        <p className="text-sm text-gray-600">Scheduled Date & Time:</p>
                        <p className="font-medium">{deliveryOrder.delivery_date} at {deliveryOrder.delivery_time}</p>
                      </div>
                    </div>
                    {deliveryOrder.pic_name && (
                      <div className="flex items-start gap-2 mb-4" data-testid="pic-display">
                        <User size={18} className="text-gray-500 mt-1" />
                        <div>
                          <p className="text-sm text-gray-600">Attn (Person-in-Charge):</p>
                          <p className="font-medium">
                            {[deliveryOrder.pic_name, deliveryOrder.pic_phone].filter(Boolean).join(' — ')}
                          </p>
                        </div>
                      </div>
                    )}
                    {deliveryOrder.pickup_contact && (
                      <div className="flex items-start gap-2 mb-4" data-testid="pickup-contact-display">
                        <User size={18} className="text-gray-500 mt-1" />
                        <div>
                          <p className="text-sm text-gray-600">Pickup Contact:</p>
                          <p className="font-medium">{deliveryOrder.pickup_contact}</p>
                        </div>
                      </div>
                    )}
                    {(deliveryOrder.site_contact || deliveryOrder.site_phone || deliveryOrder.site_contact_name || deliveryOrder.site_contact_phone) && (
                      <div className="flex items-start gap-2" data-testid="site-contact-display">
                        <User size={18} className="text-gray-500 mt-1" />
                        <div>
                          <p className="text-sm text-gray-600">Site Contact:</p>
                          <p className="font-medium">
                            {[deliveryOrder.site_contact || deliveryOrder.site_contact_name, deliveryOrder.site_phone || deliveryOrder.site_contact_phone].filter(Boolean).join(' — ')}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Items */}
              <div className="mb-8">
                <h3 className="font-semibold text-gray-700 mb-4">Items to Deliver:</h3>
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border p-3 text-left">Description</th>
                      <th className="border p-3 text-center w-24">Lorry No</th>
                      <th className="border p-3 text-center w-20">Qty</th>
                      <th className="border p-3 text-center w-20">Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(deliveryOrder.line_items || []).map((item, index) => (
                      <tr key={index}>
                        <td className="border p-3">{item.description}</td>
                        <td className="border p-3 text-center" data-testid={`do-item-lorry-${index}`}>{item.lorry_no || deliveryOrder.driver_lorry_no || deliveryOrder.vehicle_number || '-'}</td>
                        <td className="border p-3 text-center">{item.quantity}</td>
                        <td className="border p-3 text-center">{item.hours || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {deliveryOrder.notes && (
                <div className="border-t pt-6">
                  <h3 className="font-semibold text-gray-700 mb-2">Special Instructions:</h3>
                  <p className="text-gray-600">{deliveryOrder.notes}</p>
                </div>
              )}

              {/* Proof of Delivery Photos (uploaded by driver) */}
              {deliveryOrder.proof_photos && deliveryOrder.proof_photos.length > 0 && (
                <div className="border-t pt-6 mt-6 print:hidden" data-testid="proof-photos-section">
                  <h3 className="font-semibold text-gray-700 mb-3">
                    📷 Proof of Delivery Photos ({deliveryOrder.proof_photos.length})
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {deliveryOrder.proof_photos.map((p) => (
                      <a
                        key={p.id}
                        href={`${BACKEND_URL}/api/public/driver/photos/${deliveryOrder.id}/${p.id}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`proof-photo-${p.id}`}
                      >
                        <img
                          src={`${BACKEND_URL}/api/public/driver/photos/${deliveryOrder.id}/${p.id}`}
                          alt="Proof of delivery"
                          className="w-full h-32 object-cover rounded-lg border hover:opacity-90 transition-opacity"
                        />
                      </a>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-2">Photos are also included in the DO PDF.</p>
                </div>
              )}

              {/* Signing Link Share Panel */}
              <ShareSigningLink deliveryOrder={deliveryOrder} onUpdate={fetchDeliveryOrder} />

              {/* Signature Section */}
              <SignaturePanel deliveryOrder={deliveryOrder} onUpdate={fetchDeliveryOrder} />

              {/* Print-only signature display (visible when printing) */}
              <div className="border-t pt-6 mt-6 hidden print:block">
                <div className="grid grid-cols-2 gap-8">
                  <div>
                    <p className="text-sm font-semibold mb-2">Driver / Delivered By</p>
                    {deliveryOrder.driver_signature ? (
                      <img
                        src={deliveryOrder.driver_signature}
                        alt="Driver signature"
                        className="h-20 object-contain border-b border-gray-400 mb-1"
                      />
                    ) : (
                      <div className="h-20 border-b border-gray-400 mb-1"></div>
                    )}
                    <p className="text-sm text-gray-700">Name: {deliveryOrder.driver_signature_name || deliveryOrder.driver_name || ''}</p>
                    <p className="text-sm text-gray-700">Date: {deliveryOrder.driver_signed_at ? new Date(deliveryOrder.driver_signed_at).toLocaleDateString('en-SG', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</p>
                    {deliveryOrder.driver_signed_gps && (
                      <a href={`https://www.google.com/maps?q=${deliveryOrder.driver_signed_gps.lat},${deliveryOrder.driver_signed_gps.lng}`} target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1" data-testid="driver-gps-map-link">
                        📍 View on Map ({deliveryOrder.driver_signed_gps.lat}, {deliveryOrder.driver_signed_gps.lng})
                      </a>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-semibold mb-2">Received By Customer</p>
                    {deliveryOrder.customer_signature ? (
                      <img
                        src={deliveryOrder.customer_signature}
                        alt="Customer signature"
                        className="h-20 object-contain border-b border-gray-400 mb-1"
                      />
                    ) : (
                      <div className="h-20 border-b border-gray-400 mb-1"></div>
                    )}
                    <p className="text-sm text-gray-700">Name: {deliveryOrder.customer_signature_name || deliveryOrder.customer_name || ''}</p>
                    <p className="text-sm text-gray-700">Date: {deliveryOrder.customer_signed_at ? new Date(deliveryOrder.customer_signed_at).toLocaleDateString('en-SG', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</p>
                    {deliveryOrder.customer_signed_gps && (
                      <a href={`https://www.google.com/maps?q=${deliveryOrder.customer_signed_gps.lat},${deliveryOrder.customer_signed_gps.lng}`} target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1" data-testid="customer-gps-map-link">
                        📍 View on Map ({deliveryOrder.customer_signed_gps.lat}, {deliveryOrder.customer_signed_gps.lng})
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dispatch to Supplier dialog (auto-fills from DO) */}
      <DispatchSupplierDialog
        open={showDispatchModal}
        onOpenChange={setShowDispatchModal}
        sourceType="delivery_order"
        sourceId={deliveryOrder?.id || ''}
        sourceNumber={deliveryOrder?.document_number || ''}
        prefilledJob={{
          job_date: deliveryOrder?.delivery_date || '',
          pickup_location: deliveryOrder?.pickup_address || '',
          dropoff_location: deliveryOrder?.delivery_address || '',
          cargo: (deliveryOrder?.line_items || []).map(it => it.description).filter(Boolean).join(', '),
          customer_ref: `${deliveryOrder?.customer_name || ''} / ${deliveryOrder?.document_number || ''}`,
        }}
      />

      <CancelJobDialog
        open={showCancelModal}
        onOpenChange={setShowCancelModal}
        deliveryOrder={deliveryOrder}
        onCancelled={(res) => {
          fetchDeliveryOrder();
          if (res?.invoice_id) navigate(`/admin/billing/invoice/${res.invoice_id}`);
        }}
      />
    </div>
  );
};
